import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Check, X } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loading } from "@/components/common/Loading";
import { GroupInvitations } from "@/components/groups/GroupInvitations";

const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return ""; } };
const kindLabel = (kind) => (kind === "capital" ? "Capital" : "Business");

export default function Invitations() {
  const qc = useQueryClient();
  const [busyId, setBusyId] = React.useState(null);
  const [tab, setTab] = React.useState("all");
  const { data, isLoading } = useQuery({
    queryKey: ["my-team-invitations"],
    queryFn: async () => (await api.get("/team-invites/mine")).data,
  });
  const allInvites = data?.invitations || [];
  const invites = tab === "all" ? allInvites : allInvites.filter((i) => i.kind === tab);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["my-team-invitations"] });
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  const accept = async (inv) => {
    setBusyId(inv.id);
    try {
      const { data: res } = await api.post(`/team-invites/${inv.id}/accept`);
      toast.success(`You've joined ${res?.org_name || inv.org_name} as ${res?.role || inv.role}.`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "This invitation is no longer available.");
      refresh();
    } finally { setBusyId(null); }
  };

  const decline = async (inv) => {
    setBusyId(inv.id);
    try {
      await api.post(`/team-invites/${inv.id}/decline`);
      toast.success("Invitation declined.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "This invitation is no longer available.");
      refresh();
    } finally { setBusyId(null); }
  };

  if (isLoading) return <Loading />;

  const counts = {
    all: allInvites.length,
    business: allInvites.filter((i) => i.kind === "business").length,
    capital: allInvites.filter((i) => i.kind === "capital").length,
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="invitations-page">
      <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">Invitations</h1>
      <p className="mt-1 text-muted-foreground">
        Invitations to help manage a business or capital profile, and invitations to join groups.
      </p>

      {/* Groups Batch B (GRP-DEC-283): the SAME invitation record also appears in the
          Groups Pending tab — no duplicate rows are created to serve two surfaces. */}
      <GroupInvitations />

      <h2 className="mt-8 text-lg font-[650] text-foreground">Team invitations</h2>

      <Tabs value={tab} onValueChange={setTab} className="mt-5">
        <TabsList data-testid="invitations-tabs">
          <TabsTrigger value="all" data-testid="invitations-tab-all">All{counts.all ? ` (${counts.all})` : ""}</TabsTrigger>
          <TabsTrigger value="business" data-testid="invitations-tab-business">Business{counts.business ? ` (${counts.business})` : ""}</TabsTrigger>
          <TabsTrigger value="capital" data-testid="invitations-tab-capital">Capital{counts.capital ? ` (${counts.capital})` : ""}</TabsTrigger>
        </TabsList>
      </Tabs>

      {invites.length === 0 ? (
        <Card className="mt-6 p-10 text-center" data-testid="invitations-empty">
          <Mail className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">You do not have any pending invitations.</p>
        </Card>
      ) : (
        <div className="mt-6 space-y-3" data-testid="invitations-list">
          {invites.map((inv) => {
            const href = inv.org_slug ? `/${inv.kind === "capital" ? "capital" : "b"}/${inv.org_slug}` : null;
            return (
              <Card key={inv.id} className="flex items-center gap-4 p-4" data-testid={`invitation-${inv.id}`}>
                <Avatar className="h-11 w-11">
                  <AvatarImage src={inv.org_image_url || undefined} alt={inv.org_name} />
                  <AvatarFallback>{initials(inv.org_name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {href ? (
                      <Link to={href} className="truncate text-sm font-[600] text-foreground hover:underline">{inv.org_name}</Link>
                    ) : (
                      <span className="truncate text-sm font-[600] text-foreground">{inv.org_name}</span>
                    )}
                    <StatusBadge tone={inv.kind === "capital" ? "brand" : "neutral"} data-testid={`invitation-kind-${inv.id}`}>{kindLabel(inv.kind)}</StatusBadge>
                    <StatusBadge tone={inv.role === "admin" ? "info" : "neutral"}>{inv.role === "admin" ? "Admin" : "Contributor"}</StatusBadge>
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {inv.invited_by_name ? `Invited by ${inv.invited_by_name}` : "Invited"} · {fmtDate(inv.created_at)}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => decline(inv)} disabled={busyId === inv.id}
                          data-testid={`invitation-decline-${inv.id}`}>
                    <X className="mr-1 h-4 w-4" /> Decline
                  </Button>
                  <Button size="sm" onClick={() => accept(inv)} disabled={busyId === inv.id}
                          data-testid={`invitation-accept-${inv.id}`}>
                    {busyId === inv.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />} Accept
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
